import type { SessionEvent } from "@/types";
import { secondsIn, uidsIn } from "./rounds";

/**
 * A show night's record: the acts that played and the names that were drawn.
 *
 * An act is a `set_played` note — `{ title, uids?, seconds? }` — and a draw a
 * `jam_drawn` note whose `groups` are either lists of uids or objects carrying
 * `uids` (and, when the app numbered them, `number`). Both shapes are read, so
 * a draw recorded by any app version shows as a draw.
 */

export interface Act {
  id: string;
  at: number;
  title: string;
  uids: string[];
  seconds?: number;
}

export interface DrawGroup {
  number: number;
  uids: string[];
  /** Names typed on the night for people without a card, when the app sent them. */
  names: string[];
}

export interface Draw {
  id: string;
  at: number;
  groups: DrawGroup[];
}

export function acts(events: readonly SessionEvent[]): Act[] {
  return events
    .filter((event) => event.type === "set_played")
    .map((event) => {
      const title = event.payload?.title;
      const act: Act = {
        id: event.id,
        at: event.at,
        title: typeof title === "string" ? title.trim() : "",
        uids: uidsIn(event.payload),
      };
      const seconds = secondsIn(event.payload);
      if (seconds !== undefined) act.seconds = seconds;
      return act;
    })
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
}

function groupsOf(payload: Record<string, unknown> | undefined): DrawGroup[] {
  const raw = payload?.groups;
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry, index): DrawGroup[] => {
    if (Array.isArray(entry)) {
      return [{ number: index + 1, uids: entry.filter((v): v is string => typeof v === "string"), names: [] }];
    }
    if (entry && typeof entry === "object") {
      const group = entry as Record<string, unknown>;
      const number = typeof group.number === "number" ? group.number : index + 1;
      return [{ number, uids: uidsIn(group), names: uidsIn(group, "names") }];
    }
    return [];
  });
}

export function draws(events: readonly SessionEvent[]): Draw[] {
  return events
    .filter((event) => event.type === "jam_drawn")
    .map((event) => ({ id: event.id, at: event.at, groups: groupsOf(event.payload) }))
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
}
